import type { ReactNode } from "react";
import React, { useState } from "react";
import type { GridColumnVisibilityModel, GridSortModel } from "@mui/x-data-grid";
import { useFeatureFlag } from "contexts/ConfigurationProvider/ConfigurationProvider";
import { FeatureFlags } from "contexts/ConfigurationProvider/FeatureFlags";
import useAsyncAbortableRequest from "hooks/useAsyncAbortableRequest";
import useFilters from "hooks/useFilters";
import usePagination from "hooks/usePagination";
import useSorting from "hooks/useSorting";
import { nameof } from "utils/nameof";
import { LoadablePage } from "shared/LoadablePage";
import { LifecycleType } from "client/api-v2";
import type { Subscription } from "client/api-v2";
import FilterLayout from "components/FilterLayout";
import { PageContent } from "components/PageContent/PageContent";
import PageHeading from "components/PageHeading";
import type { RbacPolicy } from "components/authorization/RbacPolicies";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { SubscriptionCreateButton } from "areas/subscriptions/components/SubscriptionCreateButton";
import {
  subscriptionHeaderSections,
  subscriptionFilterSections,
  subscriptionFilterKeys,
  subscriptionDefaultFilterValue,
  subscriptionDefaultColumnVisibility,
} from "areas/subscriptions/components/SubscriptionFilters";
import { SubscriptionList } from "areas/subscriptions/components/SubscriptionList";
import { getSubscriptions } from "areas/subscriptions/services/subscriptionService";

const subscriptionActions: {
  policy: RbacPolicy;
  component?: ReactNode;
}[] = [
  {
    policy: RbacPolicies.Subscriptions.ReadWrite,
    component: <SubscriptionCreateButton />,
  },
];

export function SubscriptionPage() {
  const { page, setPage, pageSize, setPageSize } = usePagination({ initialPageSize: 50 });
  const rateCardCodeFilterEnabled = useFeatureFlag(FeatureFlags.AdoptionLedPricing);
  const { filters, setFilter, resetFilter } = useFilters({
    paramKeys: subscriptionFilterKeys,
  });

  const { sortModel, setSortModel } = useSorting({
    initialSortModel: [
      {
        field: nameof<Subscription>("termStartDate"),
        sort: "desc",
      },
    ],
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    subscriptionDefaultColumnVisibility
  );

  const {
    data = { items: [], totalItems: 0 },
    loading,
    error,
  } = useAsyncAbortableRequest(getSubscriptions, {
    // @ts-expect-error - FilterValue includes boolean/DateTime, but these filters are text/select which only produce strings
    status: filters.status ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but these filters are text/select which only produce strings
    lifecycleType: filters.lifecycleType ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is select which only produces strings
    // Guard against a stale complimentaryType value being sent when lifecycleType is not Complimentary.
    complimentaryType:
      filters.lifecycleType === LifecycleType.Complimentary ? filters.complimentaryType ?? undefined : undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is text which only produces strings
    tier: filters.tier ?? undefined,
    page: page + 1, // Convert 0-based to 1-based page number
    pageSize,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is text which only produces strings
    serial: filters.serial ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is text which only produces strings
    ownerEmail: filters.ownerEmail ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but date filters produce DateTime
    termStartAfter: filters.termStartAfter ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but date filters produce DateTime
    termStartBefore: filters.termStartBefore ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but date filters produce DateTime
    termEndAfter: filters.termEndAfter ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but date filters produce DateTime
    termEndBefore: filters.termEndBefore ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is text which only produces strings
    zuoraSubscriptionKey: filters.zuoraSubscriptionKey ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is text which only produces strings
    friendlyName: filters.friendlyName ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is text which only produces strings
    dnsPrefix: filters.dnsPrefix ?? undefined,
    // @ts-expect-error - FilterValue includes boolean/DateTime, but this filter is text which only produces strings
    rateCardCode: filters.rateCardCode ?? undefined,
    sortFieldName: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort === "asc" ? "Asc" : sortModel[0]?.sort === "desc" ? "Desc" : undefined,
  });

  // Re-sorting reorders the whole result set, so a page held from the old order shows rows nobody
  // asked for — the same hazard as changing a filter.
  const handleSortModelChange = (model: GridSortModel) => {
    setPage(0);
    setSortModel(model);
  };

  return (
    <>
      <PageHeading headingText="Subscriptions" actionItems={subscriptionActions} />
      <PageContent>
        <FilterLayout
          headerFilters={subscriptionHeaderSections}
          filterSections={subscriptionFilterSections(filters.lifecycleType, rateCardCodeFilterEnabled)}
          filter={filters}
          defaultFilter={subscriptionDefaultFilterValue}
          onFilterChange={setFilter}
          onResetFilter={resetFilter}
        >
          <LoadablePage error={error}>
            <SubscriptionList
              subscriptions={data?.items || []}
              sortModel={sortModel}
              totalCount={data?.totalItems || 0}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onSortModelChange={handleSortModelChange}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
            />
          </LoadablePage>
        </FilterLayout>
      </PageContent>
    </>
  );
}
